import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Gift, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";

const fdate = (iso) => (iso ? new Date(iso).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" }) : "");

export const GiftCard = ({ hubId, onGifted }) => {
  const [q, setQ] = useSearchParams();
  const [d, setD] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [plan, setPlan] = useState("family_plus");
  const base = `/organizations/${hubId}/billing`;
  const load = () => api.get(`${base}/gift`).then(({ data }) => setD(data)).catch(() => setD({ available: false }));
  useEffect(() => {
    const sid = q.get("session_id");
    if (q.get("gift") === "success" && sid) {
      setNote("Confirming your gift with Stripe…");
      let n = 0;
      const poll = async () => {
        n += 1;
        try {
          const { data } = await api.get(`${base}/gift-session/${sid}`);
          if (data.paid) { setNote(`Thank you! The hub is gifted until ${fdate(data.gift.until)}.`); setQ({}, { replace: true }); load(); onGifted?.(); return; }
        } catch (e) { setNote(errMsg(e)); return; }
        if (n < 10) setTimeout(poll, 2000); else setNote("Payment is still processing. Check back in a minute.");
      };
      poll();
    } else if (q.get("gift") === "cancel") { setNote("No changes were made."); setQ({}, { replace: true }); }
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const give = async () => {
    setBusy(true);
    try { const { data } = await api.post(`${base}/gift-checkout`, { origin_url: window.location.origin, plan }); window.location.href = data.url; } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  if (!d) return null;
  return (
    <section className="q-card max-w-xl space-y-4" data-testid="gift-card">
      {note && <p className="rounded-xl border border-q-line bg-q-raised px-4 py-3 text-sm font-semibold text-q-text" data-testid="gift-note">{note}</p>}
      <div className="flex items-start gap-4">
        <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-[rgba(var(--st-pink),0.15)]"><Gift className="h-6 w-6 text-[rgb(var(--st-pink))]" /></span>
        <div className="flex-1">
          <p className="font-display text-lg font-extrabold text-q-text">Gift a year of The Queue</p>
          {d.available ? (
            <>
              <p className="mt-1 text-sm text-q-muted" data-testid="gift-desc">One payment gives {d.hub_name} 12 months on the plan you pick. Gifts stack.</p>
              <div className="mt-3 grid gap-2" role="radiogroup" data-testid="gift-plan-picker">
                {(d.choices || []).map((c) => (
                  <label key={c.plan} className={`flex min-h-[44px] cursor-pointer items-center justify-between rounded-xl border px-3 ${(d.choices.length === 1 || plan === c.plan) ? "border-[rgb(var(--st-pink))] bg-[rgba(var(--st-pink),0.08)]" : "border-q-line"}`} data-testid={`gift-plan-${c.plan}`}>
                    <span className="flex items-center gap-2 text-sm font-semibold text-q-text"><input type="radio" checked={d.choices.length === 1 || plan === c.plan} onChange={() => setPlan(c.plan)} />{c.name}</span>
                    <span className="text-sm font-bold text-q-text">${(c.cents / 100).toFixed(2).replace(/\.00$/, "")}</span>
                  </label>
                ))}
              </div>
            </>
          ) : (
            <p className="mt-1 text-sm text-q-muted" data-testid="gift-unavailable">Demo hubs aren't billed, so there's nothing to gift.</p>
          )}
          {d.gift?.until && new Date(d.gift.until) > new Date() && <p className="mt-2 text-sm font-semibold text-q-text" data-testid="gift-current">Gifted by {(d.gift.by || "").split(" ")[0]} until {fdate(d.gift.until)}</p>}
        </div>
      </div>
      {d.available && (
        <button className="q-btn q-btn-primary min-h-[44px]" disabled={busy} onClick={give} data-testid="gift-checkout-btn">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Gift className="h-4 w-4" />} Gift a year
        </button>
      )}
    </section>
  );
};
